// Authentication types

export enum UserRole {
  USER = 'USER',
  ADMIN = 'ADMIN'
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  role: UserRole;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password?: string;
  role: UserRole;
  active: boolean;
}

// Zeiterfassung (Stempeluhr per QR) types

export type StempelTyp = 'KOMMEN' | 'GEHEN';

export interface StempelResponse {
  success: boolean;
  mitarbeiterName?: string;
  aktion?: StempelTyp;
  zeit?: string;        // HH:mm
  message: string;
  tagNettoMinuten?: number | null;
  tagStundenText?: string | null;
  saldoMinuten?: number | null;
  saldoText?: string | null;
  doppelt?: boolean;            // Scan kurz nach dem letzten Stempel -> nicht erneut gebucht
}

export interface StempelStatus {
  mitarbeiterName: string;
  eingestempelt: boolean;
  seit?: string | null;         // HH:mm
  letzterStempel?: string | null;
  letzterTyp?: StempelTyp | null;
}

export type KorrekturantragStatus = 'OFFEN' | 'GENEHMIGT' | 'ABGELEHNT';

export interface Korrekturantrag {
  id: number;
  userId: number;
  mitarbeiterName: string;
  typ: StempelTyp;
  zeitpunkt: string;            // yyyy-MM-dd HH:mm
  begruendung: string;
  status: KorrekturantragStatus;
  beantragtAm: string;
  entschiedenAm?: string | null;
  ablehnungsGrund?: string | null;
}

export interface KorrekturantragRequest {
  typ: StempelTyp;
  zeitpunkt: string;            // yyyy-MM-dd HH:mm
  begruendung: string;
}

export interface TagesAuswertung {
  datum: string;            // yyyy-MM-dd
  wochentag: string;        // Mo..So
  kommen?: string | null;   // HH:mm
  gehen?: string | null;    // HH:mm
  bruttoMinuten: number;
  pauseMinuten: number;
  nettoMinuten: number;
  sollMinuten: number;
  gutschriftMinuten: number;
  saldoMinuten: number;
  abwesenheit?: string | null;
  feiertag?: string | null;
  offen: boolean;
  bearbeitet: boolean;
  warnungen: string[];
}

export interface ArbeitszeitAuswertung {
  userId: number;
  mitarbeiterName: string;
  tage: TagesAuswertung[];
  summeNettoMinuten: number;
  summeSollMinuten: number;
  saldoMinuten: number;
  avgBeginn?: string | null;
  avgEnde?: string | null;
  sollBeginn?: string | null;
  sollEnde?: string | null;
}

export interface ZeiterfassungEintrag {
  id: number;
  userId: number;
  mitarbeiterName: string;
  typ: StempelTyp;
  zeitpunkt: string;        // yyyy-MM-dd HH:mm
  quelle: string;
  bearbeitet: boolean;
  notiz?: string | null;
}

export interface StempelEintragRequest {
  userId: number;
  typ: StempelTyp;
  zeitpunkt: string;        // yyyy-MM-dd HH:mm
  notiz?: string;
}

export interface StempelEintragUpdateRequest {
  typ: StempelTyp;
  zeitpunkt: string;
  notiz?: string;
}

// Arbeitszeit-Konfiguration

export interface ArbeitszeitKonfiguration {
  userId: number;
  mitarbeiterName: string;
  wochenstunden: number;
  tagesbeginn?: string | null;   // HH:mm
  tagesende?: string | null;     // HH:mm
  ausgeblendet: boolean;         // kein Mitarbeiter (Admin/System-User)
  arbeitstage: string[];         // "MO".."SO"
}

export interface ArbeitszeitKonfigurationRequest {
  wochenstunden: number;
  tagesbeginn?: string | null;
  tagesende?: string | null;
  arbeitstage?: string[];
}

// Leichtgewichtiger Mitarbeiter-Eintrag für Filter-Dropdowns (nur aktive, ohne ausgeblendete)
export interface Mitarbeiter {
  id: number;
  firstName: string;
  lastName: string;
}

// Feiertage

export interface Feiertag {
  id: number;
  datum: string;     // yyyy-MM-dd
  name: string;
  jahr: number;
  bundesland: string;
}

export interface FeiertagStatus {
  jahr: number;
  anzahl: number;
  vollstaendig: boolean;
}

// Abwesenheiten / Urlaub

export type AbwesenheitTyp = 'URLAUB' | 'KRANKHEIT' | 'UNBEZAHLT' | 'SONDERURLAUB';
export type AbwesenheitStatus = 'BEANTRAGT' | 'GENEHMIGT' | 'ABGELEHNT' | 'STORNIERT';

export interface Abwesenheit {
  id: number;
  userId: number;
  mitarbeiterName: string;
  typ: AbwesenheitTyp;
  von: string;       // yyyy-MM-dd
  bis: string;       // yyyy-MM-dd
  halberTag: boolean;
  tage: number;
  status: AbwesenheitStatus;
  antragGrund?: string | null;
  ablehnungsGrund?: string | null;
  beantragtAm?: string;
  entschiedenAm?: string;
}

export interface AbwesenheitAntragRequest {
  von: string;
  bis: string;
  halberTag?: boolean;
  antragGrund?: string;
}

export interface AbwesenheitAnlegenRequest {
  userId: number;
  typ: AbwesenheitTyp;
  von: string;
  bis: string;
  halberTag?: boolean;
  antragGrund?: string;
}

export interface AbwesenheitKonflikt {
  userId: number;
  mitarbeiterName: string;
  typ: AbwesenheitTyp;
  von: string;
  bis: string;
  status: AbwesenheitStatus;
}

export interface UrlaubSaldo {
  jahr: number;
  anspruchTage: number;
  uebertragTage: number;
  genommenTage: number;
  beantragtTage: number;
  restTage: number;
}

export interface UrlaubsanspruchRequest {
  jahr: number;
  anspruchTage: number;
  uebertragTage?: number;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

// Kunden (Customers) types

export interface Kunde {
  id: number;
  vorname?: string;
  nachnameFirma: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  mobiltelefon?: string;
  email?: string;
  entfernungKm?: number;
  anfahrtMinuten?: number;
  lat?: number;
  lng?: number;
  active?: boolean;
  wisoKundennummer?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateKundeRequest {
  vorname?: string;
  nachnameFirma: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  mobiltelefon?: string;
  email?: string;
  entfernungKm?: number;
  anfahrtMinuten?: number;
}

export interface UpdateKundeRequest {
  vorname?: string;
  nachnameFirma: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  mobiltelefon?: string;
  email?: string;
  entfernungKm?: number;
  anfahrtMinuten?: number;
}

// Waren (Products) types

/** Freies Schlüssel/Wert-Paar eines Bauteils, z. B. „Spannung“ → „230 V“. */
export interface BauteilEigenschaft {
  schluessel: string;
  wert: string;
}

export interface Bauteil {
  id: number;
  titel: string;
  eigenschaften: BauteilEigenschaft[];
  createdAt?: string;
  updatedAt?: string;
}

export interface BauteilRequest {
  titel: string;
  eigenschaften: BauteilEigenschaft[];
}

/** Kopie eines Bauteils in einer Elektroliste, pro Liste anpassbar. */
export interface ElektrolistePosition {
  titel: string;
  anzahl: number;
  eigenschaften: BauteilEigenschaft[];
}

export interface Elektroliste {
  id: number;
  kundeId: number;
  datum: string;
  titel: string;
  einleitung?: string;
  schlusstext?: string;
  positionen: ElektrolistePosition[];
  erstelltVon?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ElektrolisteRequest {
  kundeId: number;
  datum: string;
  titel: string;
  einleitung?: string;
  schlusstext?: string;
  positionen: ElektrolistePosition[];
}

export interface ElektrolisteStandardtexte {
  titel: string;
  einleitung: string;
  schlusstext: string;
}

/** Poolzeichnung: alle Maße in mm. Länge liegt in der Zeichnung waagerecht, Breite senkrecht. */
export type Seite = 'LINKS' | 'RECHTS';

/** Skimmer oder Gegenstromanlage: mittig an einer Stirnseite, Tiefe ab Innenmaß nach außen. */
export interface Einbauteil {
  breite: number;
  tiefe: number;
  seite: Seite;
}

export interface PoolMasse {
  innenLaenge: number;
  innenBreite: number;
  innenHoehe: number;
  aussenLaenge: number;
  aussenBreite: number;
  skimmer: Einbauteil | null;
  gegenstromanlage: Einbauteil | null;
  /** Abstand der Grube zum Außenmaß je Seite */
  grube: { oben: number; unten: number; links: number; rechts: number };
  schichten: { poolboden: number; isolierung: number; bodenplatte: number; drainage: number; umrandung: number; ueberstand: number };
  /** Welche Maße die Zeichnung trägt */
  sichtbar: { innen: boolean; aussen: boolean; grube: boolean };
}

export interface Poolzeichnung {
  id: number;
  kundeId: number;
  datum: string;
  titel: string;
  masse: PoolMasse;
  erstelltVon?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PoolzeichnungRequest {
  kundeId: number;
  datum: string;
  titel: string;
  masse: PoolMasse;
}

export interface Ware {
  id: number;
  artikelbezeichnung: string;
  einheit: string;
  erinnerungIntervallMonate?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateWareRequest {
  artikelbezeichnung: string;
  einheit: string;
  erinnerungIntervallMonate?: number | null;
}

export interface UpdateWareRequest {
  artikelbezeichnung: string;
  einheit: string;
  erinnerungIntervallMonate?: number | null;
}

// Erinnerungen (Reminders) types

/** Aus einem Bericht (`berichtId`, `artikelBezeichnung`) oder von Hand beim Kunden (`notiz`, Datum optional). */
export interface Erinnerung {
  id: number;
  kundeId: number;
  kundeVorname?: string;
  kundeNachnameFirma?: string;
  berichtId?: number | null;
  berichtDatum?: string | null;
  artikelBezeichnung?: string | null;
  notiz?: string | null;
  faelligAm?: string | null;
  erledigt: boolean;
  erstelltVon?: string | null;
  erstelltAm?: string;
}

export interface ErinnerungRequest {
  kundeId?: number;
  notiz: string;
  faelligAm?: string | null;
}

// Bericht Status types

export interface BerichtStatusTyp {
  id: number;
  name: string;
  farbe: string;
  reihenfolge: number;
}

// Berichte (Reports) types

export interface BerichtMaterial {
  id?: number;
  wareId?: number;
  menge: number;
  einheit: string;
  artikelbezeichnung: string;
}

export interface BerichtMitarbeiter {
  id?: number;
  userId?: number;
  name: string;
  abfahrtZeit?: string;
  ankunftZeit?: string;
  fahrzeitMinuten?: number;
  arbeitszeitStart?: string;
  arbeitszeitEnde?: string;
  pauseMinuten: number;
  gefahreneKm: number;
}

export interface Bericht {
  id: number;
  kundeId: number;
  kundeVorname?: string;
  kundeNachnameFirma?: string;
  ausgefuehrteArbeiten: string;
  abfahrtZeit: string;
  ankunftZeit: string;
  fahrzeitMinuten: number;
  arbeitszeitStart: string;
  arbeitszeitEnde: string;
  pauseMinuten: number;
  gefahreneKm: number;
  hinweise?: string;
  unterschriftBild?: string;
  unterschriftName?: string;
  kundeNichtVorOrt: boolean;
  erstelltVon?: string;
  datum: string;
  gelesen?: boolean;
  statusId?: number;
  statusName?: string;
  statusFarbe?: string;
  materialien: BerichtMaterial[];
  mitarbeiter: BerichtMitarbeiter[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBerichtRequest {
  kundeId: number;
  ausgefuehrteArbeiten: string;
  abfahrtZeit: string;
  ankunftZeit: string;
  fahrzeitMinuten: number;
  arbeitszeitStart: string;
  arbeitszeitEnde: string;
  pauseMinuten: number;
  gefahreneKm: number;
  hinweise?: string;
  unterschriftBild?: string;
  unterschriftName?: string;
  kundeNichtVorOrt: boolean;
  datum: string;
  materialien: CreateBerichtMaterialRequest[];
  mitarbeiter: CreateBerichtMitarbeiterRequest[];
}

export interface CreateBerichtMaterialRequest {
  wareId?: number;
  menge: number;
  einheit: string;
  artikelbezeichnung: string;
}

export interface CreateBerichtMitarbeiterRequest {
  userId?: number;
  name: string;
  abfahrtZeit?: string;
  ankunftZeit?: string;
  fahrzeitMinuten?: number;
  arbeitszeitStart?: string;
  arbeitszeitEnde?: string;
  pauseMinuten: number;
  gefahreneKm: number;
}

// ---- Inbetriebnahmeprotokoll (Schema + Instanzen) ----

export interface ProtokolFeld {
  id: number;
  sektionId: number;
  feldtyp: 'radio_gruppe' | 'ja_nein' | 'ja_nein_ohne' | 'text' | 'zahl' | 'freitext_liste';
  bezeichnung: string;
  optionenJson: string | null;
  reihenfolge: number;
  aktiv: boolean;
}

export interface ProtokolFeldOptionen {
  optionen?: string[];
  einheit?: string;
  placeholder?: string;
  labels?: string[];
}

export interface ProtokolSektion {
  id: number;
  buchstabe: string;
  titel: string;
  reihenfolge: number;
  aktiv: boolean;
  felder: ProtokolFeld[];
}

export interface Inbetriebnahmeprotokoll {
  id: number;
  kundeId: number;
  kundeVorname?: string;
  kundeNachnameFirma?: string;
  datum: string;
  teilnehmer?: string;
  gewaehrleistungBeginn?: string;
  maengel?: string;
  unterschriftBild?: string;
  unterschriftName?: string;
  erstelltVon?: string;
  gelesen: boolean;
  /** zwischengespeichert, noch nicht abgeschlossen */
  entwurf: boolean;
  antworten: Record<number, string | null>;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateProtokollRequest {
  kundeId: number;
  datum: string;
  teilnehmer?: string;
  gewaehrleistungBeginn?: string;
  maengel?: string;
  unterschriftBild?: string;
  unterschriftName?: string;
  antworten: Record<number, string | null>;
  entwurf?: boolean;
}

export interface UpdateProtokollRequest {
  kundeId: number;
  datum: string;
  teilnehmer?: string;
  gewaehrleistungBeginn?: string;
  maengel?: string;
  unterschriftBild?: string;
  unterschriftName?: string;
  antworten: Record<number, string | null>;
  entwurf?: boolean;
}

export interface CreateSektionRequest {
  buchstabe: string;
  titel: string;
  reihenfolge: number;
}

export interface UpdateSektionRequest {
  buchstabe: string;
  titel: string;
  reihenfolge: number;
  aktiv: boolean;
}

export interface CreateFeldRequest {
  sektionId: number;
  feldtyp: string;
  bezeichnung: string;
  optionenJson?: string | null;
  reihenfolge: number;
}

export interface UpdateFeldRequest {
  bezeichnung: string;
  feldtyp: string;
  optionenJson?: string | null;
  reihenfolge: number;
  aktiv: boolean;
}

// Aufgaben (Tasks) types

export interface AufgabeStatus {
  id: number;
  name: string;
  farbe: string;
  reihenfolge: number;
  istAbschluss: boolean;
}

export interface AufgabeVerlauf {
  id: number;
  benutzerId: number;
  benutzerName: string;
  aktion: string;
  zeitpunkt: string;
}

export interface AufgabeKommentar {
  id: number;
  benutzerId: number;
  benutzerName: string;
  text: string;
  erstelltAm: string;
}

export interface Aufgabe {
  id: number;
  thema: string;
  beschreibung?: string;
  faelligAm?: string;
  erstelltVonId: number;
  erstelltVonName: string;
  zugewiesenAnId?: number;
  zugewiesenAnName?: string;
  statusId: number;
  statusName: string;
  statusFarbe: string;
  istAbschluss: boolean;
  zuletztBearbeitetVonId?: number;
  zuletztBearbeitetVonName?: string;
  erstelltAm: string;
  zuletztBearbeitetAm?: string;
  verlauf: AufgabeVerlauf[];
  kommentare: AufgabeKommentar[];
}

export interface CreateAufgabeRequest {
  thema: string;
  beschreibung?: string;
  faelligAm?: string;
  statusId: number;
  zugewiesenAnId?: number;
}

export interface UpdateAufgabeRequest {
  thema: string;
  beschreibung?: string;
  faelligAm?: string;
  statusId: number;
  zugewiesenAnId?: number;
}

export interface CreateAufgabeStatusRequest {
  name: string;
  farbe: string;
  reihenfolge: number;
  istAbschluss: boolean;
}

export interface UpdateAufgabeStatusRequest {
  name: string;
  farbe: string;
  reihenfolge: number;
  istAbschluss: boolean;
}

// Potenzialanalyse
export type PotenzialStatus = 'NEU' | 'KONTAKTIERT' | 'ABGELEHNT' | 'UMGEWANDELT';

export interface PotenzialKunde {
  id: number;
  scoreProzent: number;
  flaecheFreiM2?: number;
  flaecheGrundstueckM2?: number;
  hauptgebaeude_m2?: number;
  bodenrichtwert?: number;
  grz?: number;
  gfkHauptgebaeude?: string;
  strasse?: string;
  hausnummer?: string;
  plz?: string;
  ort?: string;
  gemeinde?: string;
  lat?: number;
  lon?: number;
  color?: string;
  label?: string;
  status: PotenzialStatus;
  notiz?: string;
  analyseDatum?: string;
  erstelltAm?: string;
  aktualisiertAm?: string;
}

export interface UpdatePotenzialKundeRequest {
  status: PotenzialStatus;
  notiz?: string;
}

export interface PotenzialImportLog {
  id: number;
  importiert: number;
  uebersprungen: number;
  importiertVon?: string;
  importiertAm: string;
}

export interface GeoJsonImportResult {
  importiert: number;
  uebersprungen: number;
  analyseDatum: string;
}

export type PotenzialExportModus = 'TOP' | 'PLZ' | 'ORT' | 'GEMEINDE';

// Beratungstermine (Event-Anfragen)
export type BeratungsArt = 'NEUBAU' | 'SANIERUNG' | 'SERVICE_BESTAND';

export interface Berater {
  id: number;
  name: string;
  aktiv?: boolean;
  erstelltAm?: string;
}

export interface CreateBeraterRequest {
  name: string;
}

export interface Beratungstermin {
  id: number;
  vorname?: string;
  nachname: string;
  telefon: string;
  email?: string;
  plz: string;
  beratungsart: BeratungsArt;
  bemerkung?: string;
  beraterId?: number;
  beraterName?: string;
  erledigt: boolean;
  erstelltAm?: string;
}

export interface CreateBeratungsterminRequest {
  vorname?: string;
  nachname: string;
  telefon: string;
  email?: string;
  plz: string;
  beratungsart: BeratungsArt;
  bemerkung?: string;
  beraterId?: number;
}
